import { PrintInfo } from "common/api/decimal/methods/course";
import { CourseMaterial } from "../coursesLoader";
import { useLayoutData } from "../../layout/myCoursesLayoutLoader";
import { FormattedMessage, useIntl } from "react-intl";
import styles from "./PrintDetails.module.scss";
import {
  optedOutWithoutBook,
  optedOutWithBook,
  isPickupDateInFuture,
} from "../printStatusHelper";

export default function PrintDetails({
  printInfo,
  courseMaterial,
}: {
  printInfo: PrintInfo;
  courseMaterial: CourseMaterial;
}) {
  const intl = useIntl();
  const {
    config: {
      assets: { showPrintPickup },
    },
  } = useLayoutData();
  const { returnDate, pickupDate, location, hours } = printInfo;
  const { materialFormat, printStatus, replacementCost } = courseMaterial;
  const cost = intl.formatNumber(replacementCost ?? 0, {
    style: "currency",
    currency: "USD",
  });
  const returnDateAsDate = new Date(returnDate);
  const pickupDateAsDate = pickupDate ? new Date(pickupDate) : null;
  const returnDateHasPassed = returnDateAsDate < new Date();
  const pickupDateInFuture = isPickupDateInFuture(pickupDate);

  if (
    materialFormat !== "print" ||
    returnDateHasPassed ||
    optedOutWithoutBook(printStatus) ||
    !showPrintPickup
  ) {
    return null;
  }

  return (
    <>
      <dt data-testid="print-status">
        <FormattedMessage defaultMessage="Pick up" />
      </dt>
      <dd>
        {printStatus === "Not Issued" && (
          <>
            {pickupDateInFuture ? (
              <div className={styles.details}>
                <div>
                  <FormattedMessage defaultMessage="Preparing for Campus Pickup" />
                </div>
                <div>
                  <FormattedMessage
                    defaultMessage="This book will be available to pick up starting {pickupDate, date, long}."
                    values={{ pickupDate: pickupDateAsDate }}
                  />
                </div>
              </div>
            ) : (
              <div className={styles.details}>
                <div>
                  <FormattedMessage defaultMessage="Ready for Campus Pickup" />
                </div>
                <div>
                  <FormattedMessage defaultMessage="Visit your campus bookstore to pick up your print book." />
                </div>
              </div>
            )}
          </>
        )}
        {printStatus === "Issued" && (
          <div>
            <FormattedMessage defaultMessage="Already Picked Up" />
          </div>
        )}
        {printStatus === "Not Returned" && (
          <div className={styles.details}>
            <div>
              <FormattedMessage defaultMessage="Needs to be Returned" />
            </div>
            <div>
              <FormattedMessage
                defaultMessage="This print book needs to be returned to the bookstore by {returnDate, date, long} or your account will be charged {cost}."
                values={{
                  returnDate: returnDateAsDate,
                  cost,
                }}
              />
            </div>
          </div>
        )}
        {printStatus === "Return Refused" && (
          <div className={styles.details}>
            <div>
              <FormattedMessage defaultMessage="Return Refused" />
            </div>
            <div>
              <FormattedMessage defaultMessage="Your book is not returnable." />
            </div>
          </div>
        )}
      </dd>

      {printStatus !== "Issued" && printStatus !== "Return Refused" && (
        <>
          <dt>
            <div className={styles.locationTitle}>
              <FormattedMessage defaultMessage="Bookstore Location & Hours" />
            </div>
          </dt>
          <dd>
            <div className={styles.details}>
              <div>{location}</div>
              <div>
                <FormattedMessage
                  defaultMessage="Hours: {hours}"
                  values={{ hours }}
                />
              </div>
              <div>
                {optedOutWithBook(printStatus) ? (
                  <FormattedMessage
                    defaultMessage="Return by: {returnDateAsDate, date, long}"
                    values={{ returnDateAsDate }}
                  />
                ) : pickupDateAsDate ? (
                  <FormattedMessage
                    defaultMessage="Pickup Window: {pickupDateAsDate, date, long} - {returnDateAsDate, date, long}"
                    values={{ pickupDateAsDate, returnDateAsDate }}
                  />
                ) : null}
              </div>
            </div>
          </dd>
        </>
      )}
    </>
  );
}
